import { useCallback } from 'react';
import * as XLSX from 'xlsx';
import { supabase } from '../lib/supabase';
import { useToast } from './useToast';
import { useConfirm } from './useConfirm';
import { formatDateDB } from '../utils/dateUtils';
import type { MaterialCatalog, PriceHistory } from '../types';
import type {
  BLReference,
  ComparedPrice,
  PriceFormState,
  NewMaterialState,
  Supplier,
} from './usePriceComparatorData';

interface UsePriceComparatorActionsParams {
  catalog: MaterialCatalog[];
  suppliers: Supplier[];
  searchQuery: string;
  setSearchResults: (results: ComparedPrice[]) => void;
  setSearching: (v: boolean) => void;
  newMaterial: NewMaterialState;
  setNewMaterial: (v: NewMaterialState) => void;
  setShowAddForm: (v: boolean) => void;
  priceForm: PriceFormState;
  setPriceForm: (v: PriceFormState) => void;
  setEditingPriceFor: (v: string | null) => void;
  historyMaterialId: string | null;
  setHistoryMaterialId: (v: string | null) => void;
  setPriceHistoryData: (v: PriceHistory[]) => void;
  blMaterialId: string | null;
  setBlMaterialId: (v: string | null) => void;
  setBlReferences: (v: BLReference[]) => void;
  setImporting: (v: boolean) => void;
  fetchData: () => Promise<void>;
}

export function usePriceComparatorActions(params: UsePriceComparatorActionsParams) {
  const {
    catalog, suppliers, searchQuery, setSearchResults, setSearching,
    newMaterial, setNewMaterial, setShowAddForm,
    priceForm, setPriceForm, setEditingPriceFor,
    historyMaterialId, setHistoryMaterialId, setPriceHistoryData,
    blMaterialId, setBlMaterialId, setBlReferences,
    setImporting, fetchData,
  } = params;

  const { toast } = useToast();
  const { confirm: confirmDialog } = useConfirm();

  // ─── RECHERCHE & COMPARAISON ─────────────────────────────────────────
  const handleSearch = useCallback(async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);

    // Chercher dans le catalogue
    const { data: materials } = await supabase
      .from('material_catalog')
      .select('*')
      .eq('is_active', true)
      .or(`reference.ilike.%${searchQuery}%,designation.ilike.%${searchQuery}%,brand.ilike.%${searchQuery}%`)
      .limit(50);

    if (!materials || materials.length === 0) {
      setSearchResults([]);
      setSearching(false);
      return;
    }

    // Récupérer les prix pour ces matériels
    const materialIds = materials.map(m => m.id);
    const { data: prices } = await supabase
      .from('supplier_prices')
      .select('*, supplier:suppliers(id, name)')
      .in('material_id', materialIds)
      .eq('is_active', true)
      .order('net_price', { ascending: true });

    // Construire les comparaisons
    const compared: ComparedPrice[] = materials.map(material => {
      const materialPrices = (prices || [])
        .filter(p => p.material_id === material.id)
        .map(p => ({ ...p, supplier_name: p.supplier?.name || 'Inconnu' }));

      const netPrices = materialPrices.map(p => p.net_price || p.catalog_price || Infinity).filter(p => p !== Infinity);
      const bestPrice = netPrices.length > 0 ? Math.min(...netPrices) : undefined;
      const maxPrice = netPrices.length > 1 ? Math.max(...netPrices) : undefined;
      const bestSupplierId = materialPrices.find(p => (p.net_price || p.catalog_price) === bestPrice)?.supplier_id;
      const savings = bestPrice && maxPrice ? maxPrice - bestPrice : undefined;

      return { material, prices: materialPrices, bestPrice, bestSupplierId, maxPrice, savings };
    });

    setSearchResults(compared);
    setSearching(false);
  }, [searchQuery, setSearchResults, setSearching]);

  // ─── AJOUT MATÉRIEL ──────────────────────────────────────────────────
  const handleAddMaterial = async () => {
    if (!newMaterial.reference || !newMaterial.designation) return;
    const { error } = await supabase.from('material_catalog').insert({
      reference: newMaterial.reference,
      designation: newMaterial.designation,
      brand: newMaterial.brand || null,
      category: newMaterial.category || null,
      unit: newMaterial.unit || 'pce'
    });
    if (error) {
      if (error.code === '23505') toast.warning('Cette référence existe déjà dans le catalogue');
      else toast.error('Erreur: ' + error.message);
      return;
    }
    toast.success('Article ajouté au catalogue.');
    setNewMaterial({ reference: '', designation: '', brand: '', category: '', unit: 'pce' });
    setShowAddForm(false);
    fetchData();
  };

  // ─── SUPPRESSION MATÉRIEL ────────────────────────────────────────────
  const handleDeleteMaterial = async (id: string) => {
    const ok = await confirmDialog({ title: "Supprimer cet article ?", message: "L'article sera retiré du catalogue.", variant: "danger" });
    if (!ok) return;
    const { error } = await supabase.from('material_catalog').update({ is_active: false }).eq('id', id);
    if (error) { toast.error('Erreur: ' + error.message); return; }
    toast.success('Article retiré du catalogue.');
    fetchData();
  };

  // ─── AJOUT PRIX ──────────────────────────────────────────────────────
  const handleSavePrice = async (materialId: string) => {
    if (!priceForm.supplier_id) return;
    const { error } = await supabase.from('supplier_prices').upsert({
      material_id: materialId,
      supplier_id: priceForm.supplier_id,
      catalog_price: priceForm.catalog_price ? parseFloat(priceForm.catalog_price) : null,
      net_price: priceForm.net_price ? parseFloat(priceForm.net_price) : null,
      discount_percent: priceForm.catalog_price && priceForm.net_price
        ? ((1 - parseFloat(priceForm.net_price) / parseFloat(priceForm.catalog_price)) * 100)
        : null,
      stock_status: priceForm.stock_status,
      delivery_days: priceForm.delivery_days ? parseInt(priceForm.delivery_days) : null,
      effective_date: formatDateDB(new Date()),
      is_active: true,
      source: 'manual'
    }, { onConflict: 'material_id,supplier_id,effective_date' });

    if (error) { toast.error('Erreur: ' + error.message); return; }
    toast.success('Prix enregistré.');
    setEditingPriceFor(null);
    setPriceForm({ supplier_id: '', catalog_price: '', net_price: '', stock_status: 'unknown', delivery_days: '' });
  };

  // ─── HISTORIQUE PRIX ─────────────────────────────────────────────────
  const loadHistory = async (materialId: string) => {
    if (historyMaterialId === materialId) { setHistoryMaterialId(null); return; }
    setHistoryMaterialId(materialId);
    const { data } = await supabase
      .from('price_history')
      .select('*, supplier:suppliers(id, name)')
      .eq('material_id', materialId)
      .order('recorded_at', { ascending: true })
      .limit(200);
    setPriceHistoryData(data || []);
  };

  // ─── CROSS-RÉFÉRENCE BL ────────────────────────────────────────────
  const loadBLReferences = async (materialId: string) => {
    if (blMaterialId === materialId) { setBlMaterialId(null); return; }
    setBlMaterialId(materialId);
    const { data } = await supabase
      .from('purchase_lines')
      .select('quantity, unit_price, purchase:purchases(bl_number, bl_date, supplier:suppliers(name), project:projects(name))')
      .eq('material_id', materialId)
      .order('created_at', { ascending: false })
      .limit(10);

    if (data) {
      setBlReferences(data.map((line: Record<string, unknown>) => {
        const purchase = line.purchase as Record<string, unknown> | null;
        const supplier = purchase?.supplier as { name: string } | null;
        const project = purchase?.project as { name: string } | null;
        return {
          bl_number: (purchase?.bl_number as string) || '—',
          bl_date: (purchase?.bl_date as string) || '—',
          supplier_name: supplier?.name || '—',
          project_name: project?.name || '—',
          quantity: line.quantity as number,
          unit_price: line.unit_price as number
        };
      }));
    } else {
      setBlReferences([]);
    }
  };

  // ─── IMPORT EXCEL CATALOGUE ──────────────────────────────────────────
  const handleImportCatalog = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);

    try {
      const buffer = await file.arrayBuffer();
      const wb = XLSX.read(buffer);
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json<Record<string, string>>(ws);

      const materials = rows
        .filter(r => r['reference'] || r['Reference'] || r['REF'])
        .map(r => ({
          reference: r['reference'] || r['Reference'] || r['REF'] || '',
          designation: r['designation'] || r['Designation'] || r['DESIGNATION'] || r['description'] || '',
          brand: r['brand'] || r['Marque'] || r['marque'] || null,
          category: r['category'] || r['Categorie'] || r['categorie'] || null,
          unit: r['unit'] || r['Unite'] || r['unite'] || 'pce'
        }))
        .filter(m => m.reference && m.designation);

      if (materials.length === 0) {
        toast.warning('Aucun article trouvé. Vérifiez que le fichier contient des colonnes "reference" et "designation".');
        return;
      }

      const { error } = await supabase.from('material_catalog').upsert(materials, { onConflict: 'reference' });
      if (error) { toast.error('Erreur import: ' + error.message); return; }

      toast.success(`${materials.length} articles importés avec succès`);
      fetchData();
    } catch (err) {
      toast.error('Erreur de lecture du fichier Excel');
      console.error(err);
    } finally {
      setImporting(false);
      e.target.value = '';
    }
  };

  // ─── IMPORT EXCEL PRIX ───────────────────────────────────────────────
  const handleImportPrices = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);

    try {
      const buffer = await file.arrayBuffer();
      const wb = XLSX.read(buffer);
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json<Record<string, string | number>>(ws);

      let imported = 0;
      for (const row of rows) {
        const reference = String(row['reference'] || row['Reference'] || row['REF'] || '');
        const supplierName = String(row['fournisseur'] || row['Fournisseur'] || row['supplier'] || '');
        if (!reference || !supplierName) continue;

        // Trouver le matériel
        const material = catalog.find(m => m.reference.toLowerCase() === reference.toLowerCase());
        if (!material) continue;

        // Trouver le fournisseur
        const supplier = suppliers.find(s => s.name.toLowerCase() === supplierName.toLowerCase());
        if (!supplier) continue;

        const catalogPrice = parseFloat(String(row['prix_catalogue'] || row['catalog_price'] || 0));
        const netPrice = parseFloat(String(row['prix_net'] || row['net_price'] || row['prix'] || 0));

        await supabase.from('supplier_prices').upsert({
          material_id: material.id,
          supplier_id: supplier.id,
          catalog_price: catalogPrice || null,
          net_price: netPrice || null,
          discount_percent: catalogPrice && netPrice ? ((1 - netPrice / catalogPrice) * 100) : null,
          effective_date: formatDateDB(new Date()),
          is_active: true,
          source: 'import'
        }, { onConflict: 'material_id,supplier_id,effective_date' });

        imported++;
      }

      toast.success(`${imported} prix importés sur ${rows.length} lignes`);
      fetchData();
    } catch (err) {
      toast.error('Erreur de lecture du fichier');
      console.error(err);
    } finally {
      setImporting(false);
      e.target.value = '';
    }
  };

  // ─── EXPORT CATALOGUE ────────────────────────────────────────────────
  const exportCatalog = () => {
    const rows = catalog.map(m => ({
      reference: m.reference,
      designation: m.designation,
      marque: m.brand || '',
      categorie: m.category || '',
      unite: m.unit
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Catalogue');
    XLSX.writeFile(wb, 'catalogue_materiel.xlsx');
  };

  return {
    handleSearch,
    handleAddMaterial,
    handleDeleteMaterial,
    handleSavePrice,
    loadHistory,
    loadBLReferences,
    handleImportCatalog,
    handleImportPrices,
    exportCatalog,
  };
}
